import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../index.css';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Map marker update component
const MarkerPosition = ({ position, onMapClick }) => {
    const map = useMap();
    
    useEffect(() => {
        map.setView(position, map.getZoom());
    }, [position, map]);

    useMapEvents({
        click: (e) => {
            onMapClick(e);
        },
    });

    return <Marker position={position} />;
};

const VineyardForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [availableVarietals, setAvailableVarietals] = useState([]); // Dynamic varietals
    const [vineyard, setVineyard] = useState({
        companyID: '',
        vineyardName: '',
        location: '',
        addressLine: '',
        city: '',
        state: '',
        zip: '',
        latitude: 38.1341197, // Default to somewhere in wine country
        longitude: -121.2722148,
        acreage: '',
        varietals: [],
        notes: '',
        vinAppellation: '',
        vinSubAppellation: '',
        vinBlock: ''
    });
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [mapPosition, setMapPosition] = useState([38.1341197, -121.2722148]);

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const response = await axios.get('/api/companies', {
                    params: {
                        type: 'grower'
                    }
                });
                setCompanies(response.data);
            } catch (error) {
                console.error('Error fetching companies:', error);
                alert('Error loading companies');
            }
        };

        const fetchVarietals = async () => {
            try {
                const response = await axios.get('/api/varietals'); // Fetch varietals dynamically
                setAvailableVarietals(response.data.sort()); // Sort alphabetically
            } catch (error) {
                console.error('Error fetching varietals:', error);
                alert('Error loading varietals');
            }
        };

        fetchCompanies();
        fetchVarietals();

        if (id) {
            fetchVineyard();
        }
    }, [id]);

    const fetchVineyard = async () => {
        try {
            const response = await axios.get(`/api/vineyards/${id}`);
            const vineyardData = response.data;
            
            const varietalsArray = vineyardData.varietals ? 
                vineyardData.varietals.split(',').map(v => v.trim()) : [];

            setVineyard({
                ...vineyardData,
                varietals: varietalsArray
            });

            if (vineyardData.latitude && vineyardData.longitude) {
                setMapPosition([vineyardData.latitude, vineyardData.longitude]);
            }
                if (vineyardData.companyID) {
                        try {
                            const companyResponse = await axios.get(`/api/companies/${vineyardData.companyID}`);
                            console.log('Company Data:', companyResponse.data);
                            setSelectedCompany(companyResponse.data);
                            
                            // Also update the vineyard's companyID to ensure the select is populated
                            setVineyard(prev => ({
                                ...prev,
                                companyID: vineyardData.companyID
                            }));
                        } catch (error) {
                            console.error('Error fetching company details:', error);
                        }
                    }
                } catch (error) {
                    console.error('Error fetching vineyard:', error);
                    alert('Error loading vineyard data');
                }
            };

    const handleVarietalChange = (e) => {
        const value = e.target.value;
        setVineyard(prev => ({
            ...prev,
            varietals: prev.varietals.includes(value)
                ? prev.varietals.filter(v => v !== value)
                : [...prev.varietals, value]
        }));
    };

    const handleCompanyChange = async (e) => {
        const companyId = e.target.value;
        
        // Update vineyard state with new companyID
        setVineyard(prev => ({ ...prev, companyID: companyId }));
    
        if (companyId) {
            try {
                const response = await axios.get(`/api/companies/${companyId}`);
                console.log('Selected Company Data:', response.data);
                setSelectedCompany(response.data);
            } catch (error) {
                console.error('Error fetching company details:', error);
                setSelectedCompany(null);
            }
        } else {
            setSelectedCompany(null);
        }
    };

    const geocodeAddress = async () => {
        const address = `${vineyard.addressLine}, ${vineyard.city}, ${vineyard.state} ${vineyard.zip}`;
        try {
            const response = await axios.get(`https://nominatim.openstreetmap.org/search`, {
                params: {
                    format: 'json',
                    q: address,
                    limit: 1
                },
                headers: {
                    'User-Agent': 'WineryManagementApp'
                }
            });
            
            if (response.data && response.data[0]) {
                const { lat, lon } = response.data[0];
                setVineyard(prev => ({
                    ...prev,
                    latitude: parseFloat(lat),
                    longitude: parseFloat(lon)
                }));
                setMapPosition([parseFloat(lat), parseFloat(lon)]);
            } else {
                alert('Address not found. Please check the address or set location manually on map.');
            }
        } catch (error) {
            console.error('Geocoding error:', error);
            alert('Error finding address. Please try again or set location manually.');
        }
    };

    const handleMapClick = (e) => {
        const { lat, lng } = e.latlng;
        setVineyard(prev => ({
            ...prev,
            latitude: lat,
            longitude: lng
        }));
        setMapPosition([lat, lng]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const submitData = {
                ...vineyard,
                varietals: vineyard.varietals.join(', '),
                vinAppellation: vineyard.vinAppellation,
                vinSubAppellation: vineyard.vinSubAppellation,
                vinBlock: vineyard.vinBlock
            };
    
            if (id) {
                await axios.put(`/api/vineyards/${id}`, submitData);
            } else {
                await axios.post('/api/vineyards', submitData);
            }
            alert(`Vineyard ${id ? 'updated' : 'created'} successfully`);
            navigate('/vineyards');
        } catch (error) {
            console.error('Error saving vineyard:', error);
            alert('Error saving vineyard data');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container mt-4">
            <h2>{id ? 'Edit' : 'New'} Vineyard</h2>
            <form onSubmit={handleSubmit}>
                <div className="row">
                    <div className="col-md-8">
                        <div className="card mb-4">
                            <div className="card-body">
                                {/* Company Selection */}
                                <div className="mb-3">
                                    <label className="form-label">Company *</label>
                                    <select
                                        className="form-control"
                                        value={vineyard.companyID}
                                        onChange={handleCompanyChange}
                                        required
                                    >
                                        <option value="">Select a Company</option>
                                        {companies.map(company => (
                                            <option key={company.id} value={company.id}>
                                                {company.companyName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Company Details */}
                                {selectedCompany && (
                                    <div className="mb-3 p-3 bg-light rounded">
                                        <h6>Company Contact Information:</h6>
                                        <p className="mb-1">
                                            Contact: {selectedCompany.firstName} {selectedCompany.lastName}
                                        </p>
                                        <p className="mb-1">Phone: {selectedCompany.phone}</p>
                                        <p className="mb-1">Email: {selectedCompany.email}</p>
                                        {selectedCompany.addresses?.map((address, idx) => (
                                            <p key={idx} className="mb-1">
                                                {address.addressType}: {address.addressLine}, {address.city}, 
                                                {address.state} {address.zip}
                                            </p>
                                        ))}
                                    </div>
                                )}

                                {/* Basic Information */}
                                <div className="mb-3">
                                    <label className="form-label">Vineyard Name *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={vineyard.vineyardName}
                                        onChange={(e) => setVineyard({ ...vineyard, vineyardName: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Appellation</label>
                                    <select
                                        className="form-control"
                                        value={vineyard.vinAppellation}
                                        onChange={(e) => setVineyard({ ...vineyard, vinAppellation: e.target.value })}
                                    >
                                        <option value="">Select an Appellation</option>
                                        <option value="Lodi">Lodi</option>
                                        <option value="California">California</option>
                                        <option value="Amador County">Amador County</option>
                                        <option value="Napa">Napa</option>
                                        <option value="Napa">Sonoma</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Sub-Appellation</label>
                                    <select
                                        className="form-control"
                                        value={vineyard.vinSubAppellation}
                                        onChange={(e) => setVineyard({ ...vineyard, vinSubAppellation: e.target.value })}
                                    >
                                        <option value="">Select a Sub-Appellation</option>
                                        <option value="Alta Mesa">Alta Mesa</option>
                                        <option value="Borden Ranch">Borden Ranch</option>
                                        <option value="Clements Hills">Clements Hills</option>
                                        <option value="Cosumnes River">Cosumnes River</option>
                                        <option value="Jahant">Jahant</option>
                                        <option value="Mokelumne River">Mokelumne River</option>
                                        <option value="Sloughhouse">Sloughhouse</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Block</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={vineyard.vinBlock}
                                        onChange={(e) => setVineyard({ ...vineyard, vinBlock: e.target.value })}
                                        placeholder="Enter vineyard block"
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Location Description</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={vineyard.location}
                                        onChange={(e) => setVineyard({ ...vineyard, location: e.target.value })}
                                        placeholder="e.g., Southeast Corner"
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Acreage</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        className="form-control"
                                        value={vineyard.acreage}
                                        onChange={(e) => setVineyard({ ...vineyard, acreage: e.target.value })}
                                    />
                                </div>

                                {/* Address Fields */}
                                <div className="mb-3">
                                    <label className="form-label">Street Address</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={vineyard.addressLine}
                                        onChange={(e) => setVineyard({ ...vineyard, addressLine: e.target.value })}
                                    />
                                </div>

                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">City</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={vineyard.city}
                                            onChange={(e) => setVineyard({ ...vineyard, city: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-3 mb-3">
                                        <label className="form-label">State</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={vineyard.state}
                                            onChange={(e) => setVineyard({ ...vineyard, state: e.target.value })}
                                        />
                                    </div>
                                    <div className="col-md-3 mb-3">
                                        <label className="form-label">ZIP Code</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={vineyard.zip}
                                            onChange={(e) => setVineyard({ ...vineyard, zip: e.target.value })}
                                        />
                                    </div>
                                </div>

                                {/* Map Section */}
                                <div className="mb-3">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={geocodeAddress}
                                    >
                                        Find on Map
                                    </button>
                                </div>

                                <div className="mb-3" style={{ height: '400px' }}>
                                    <MapContainer
                                        center={mapPosition}
                                        zoom={13}
                                        style={{ height: '100%', width: '100%' }}
                                    >
                                        <TileLayer
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        />
                                        <MarkerPosition position={mapPosition} onMapClick={handleMapClick} />
                                    </MapContainer>
                                </div>

                                {/* Coordinates */}
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Latitude</label>
                                        <input
                                            type="number"
                                            step="any"
                                            className="form-control"
                                            value={vineyard.latitude}
                                            onChange={(e) => {
                                                const lat = parseFloat(e.target.value);
                                                setVineyard({ ...vineyard, latitude: lat });
                                                setMapPosition([lat, vineyard.longitude]);
                                            }}
                                        />
                                    </div>
                                {/* Coordinates */}
                                <div className="mb-3">
                                    <label className="form-label">Coordinates (Latitude, Longitude)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={vineyard.latitude && vineyard.longitude 
                                            ? `${vineyard.latitude}, ${vineyard.longitude}` 
                                            : ''}
                                        onChange={(e) => {
                                            // Trim and split the input
                                            const trimmedCoords = e.target.value.trim();
                                            const coordParts = trimmedCoords.split(',');
                                            
                                            // Validate and parse coordinates
                                            if (coordParts.length === 2) {
                                                const lat = parseFloat(coordParts[0].trim());
                                                const lng = parseFloat(coordParts[1].trim());
                                                
                                                if (!isNaN(lat) && !isNaN(lng)) {
                                                    setVineyard(prev => ({
                                                        ...prev,
                                                        latitude: lat,
                                                        longitude: lng
                                                    }));
                                                    setMapPosition([lat, lng]);
                                                }
                                            }
                                        }}
                                        placeholder="e.g., 38.1787017818039, -121.11165694707303"
                                    />
                                    <small className="form-text text-muted">
                                        Paste coordinates from Google Maps or enter manually
                                    </small>
                                </div>
                                </div>

                                {/* Notes Field */}
                                <div className="mb-3">
                                    <label className="form-label">Notes</label>
                                    <textarea
                                        className="form-control"
                                        rows="4"
                                        value={vineyard.notes}
                                        onChange={(e) => setVineyard({ ...vineyard, notes: e.target.value })}
                                        placeholder="Enter any additional notes about the vineyard..."
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Varietals Section */}
                    <div className="col-md-4">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Varietals</h5>
                                <div className="mb-3">
                                    {availableVarietals.map(varietal => (
                                        <div key={varietal} className="form-check">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id={`varietal-${varietal}`}
                                                value={varietal}
                                                checked={vineyard.varietals.includes(varietal)}
                                                onChange={handleVarietalChange}
                                            />
                                            <label 
                                                className="form-check-label" 
                                                htmlFor={`varietal-${varietal}`}
                                            >
                                                {varietal}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Submit Buttons */}
                <div className="mt-4">
                    <button 
                        type="submit" 
                        className="btn btn-primary me-2"
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : 'Save Vineyard'}
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate('/vineyards')}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default VineyardForm;